// HOME page — landing. Pitches the game and routes new players into onboarding
// via the PLAY page (where the onboarding modal lives).

import { Events, on } from '../eventBus.js'
import { setActivePage } from '../siteRouter.js'
import { getProfile } from '../../game/state.js'
import { openTrailer } from '../trailerModal.js'

// Hero headline. Everything but the last word is the small top line; the last
// word is the big line. Both lines are auto-fitted to the same width.
const HEADLINE = 'CAPITALISM GONE AMOK!'

function renderHeadline(text) {
  const words = text.trim().split(/\s+/)
  const big = words.length > 1 ? words.pop() : ''
  const top = words.join(' ')
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;')
  return (top ? `<span class="amok-top">${esc(top)}</span>` : '') +
         (big ? `<span class="amok-big">${esc(big)}</span>` : '')
}

// Scale each line's font-size so its rendered width equals the block's width.
function fitHeadline(el) {
  const target = el.clientWidth
  if (!target) return // page hidden (display:none) — ResizeObserver re-runs when shown
  el.querySelectorAll('span').forEach((line) => {
    line.style.fontSize = '100px'
    const w = line.offsetWidth
    if (w) line.style.fontSize = `${(100 * target) / w}px`
  })
}

export function mountHomePage({ root }) {
  root.innerHTML = `
    <div class="page-home">
      <img class="page-home-art" data-home-art src="/images/mao-hero-v3.webp" alt="" aria-hidden="true">
      <div class="page-home-hero">
        <img class="page-home-logo" data-home-logo src="/mao-kingdom-logo.png" alt="MAO KINGDOM 猫王国">
        <h1 class="page-home-logo-fallback" data-home-logo-fallback>MAO KINGDOM <span>猫王国</span></h1>
        <h2 class="page-home-amok" data-home-amok>${renderHeadline(HEADLINE)}</h2>
        <p class="page-home-tagline">
          A low-poly hyper clicker of cats, rice, and dynasty. Will you choose
          greed, revenge, or compassion? Adopt your cat, choose your kingdom —
          Wei, Han, or Wu — or forge a new kingdom. Gain wealth and rise to
          glory, from peasant farmer to royalty through the seasons of the
          Three Kingdoms.
        </p>
        <div class="page-home-actions">
          <button class="page-home-trailer" data-home-trailer type="button">WATCH TRAILER</button>
          <button class="page-home-cta" data-home-cta>ADOPT A MAO</button>
        </div>
      </div>
      <div class="page-home-grid">
        <div class="page-home-card">
          <div class="page-home-card-title">CLICK THE PAW</div>
          <p>Tend the rice paddy through four seasons. PREP, PLANT, MAINTAIN, HARVEST — each click moves you closer to fortune.</p>
        </div>
        <div class="page-home-card">
          <div class="page-home-card-title">RAISE A KINGDOM</div>
          <p>Hire peasant and scribe cats, build granaries and temples, and watch your fields work themselves while you study.</p>
        </div>
        <div class="page-home-card">
          <div class="page-home-card-title">CLIMB THE COURT</div>
          <p>Earn diplomas, win licenses, contest election seats. Become a noble of your kingdom — or defect to another for a price.</p>
        </div>
      </div>
    </div>
  `

  // Show the styled text wordmark only if the logo image is missing/unreachable,
  // so the hero never renders a broken-image icon.
  const logo = root.querySelector('[data-home-logo]')
  const logoFallback = root.querySelector('[data-home-logo-fallback]')
  logo.addEventListener('error', () => {
    logo.style.display = 'none'
    logoFallback.style.display = 'block'
  })

  // Hero character art is optional until the final PNG is dropped in /public/images.
  const art = root.querySelector('[data-home-art]')
  const page = root.querySelector('.page-home')
  art.addEventListener('load', () => page.classList.add('has-art'))
  art.addEventListener('error', () => { art.style.display = 'none' })
  if (art.complete && art.naturalWidth > 0) page.classList.add('has-art')

  const amok = root.querySelector('[data-home-amok]')
  const fit = () => fitHeadline(amok)
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(fit).observe(amok)
  if (document.fonts?.ready) document.fonts.ready.then(fit)
  if (document.fonts?.load) document.fonts.load('100px Harmone').then(fit, () => {}) // the headline is set in Harmone: fit it again once that has loaded (its letters are a different width from the fallback's)
  fit()

  const cta = root.querySelector('[data-home-cta]')
  const refresh = () => {
    cta.textContent = getProfile().onboarded ? 'ENTER YOUR KINGDOM' : 'ADOPT A MAO'
  }

  cta.addEventListener('click', () => setActivePage('play'))
  root.querySelector('[data-home-trailer]').addEventListener('click', openTrailer) // the trailer plays in a full-window pop-up
  on(Events.ProfileChange, refresh)
  on(Events.OnboardingComplete, refresh)
  refresh()
}
